<script lang="ts">
  // Editor section "Tijd": date, start, end, pause and the resulting duration
  // with the rounding that applies.
  import '../../messages/entries';
  import { Clock, Moon, Sparkles } from '@lucide/svelte';
  import { Badge, Field, Input, TimeInput } from '../../ui';
  import { t } from '../../i18n.svelte';
  import { formatDate, formatDuration } from '../../format';
  import { nowRounded, roundingActive } from '../helpers';
  import EditorSection from './EditorSection.svelte';
  import type { EntryForm } from './form.svelte';

  let { form }: { form: EntryForm } = $props();

  const snap = $derived(form.editing?.rounding_applied ? form.editing.rounding_settings_snapshot : null);
</script>

<EditorSection title={t('entries.section_time')} icon={Clock}>
  <div class="times">
    <div class="date">
      <Field label={t('field.date')} for={form.id('date')} error={form.show('date')}>
        <Input id={form.id('date')} type="date" bind:value={form.date} invalid={!!form.show('date')} required />
      </Field>
    </div>
    <Field label={t('field.start')} for={form.id('start')} error={form.show('start')}>
      <TimeInput id={form.id('start')} bind:value={form.start} invalid={!!form.show('start')} ariaLabel={t('field.start')} />
    </Field>
    <Field label={t('field.end')} for={form.id('end')} error={form.show('end')}>
      {#snippet aside()}
        <button type="button" class="now focus-ring" onclick={() => (form.end = nowRounded())}>{t('entries.now')}</button>
      {/snippet}
      <TimeInput id={form.id('end')} bind:value={form.end} invalid={!!form.show('end')} ariaLabel={t('field.end')} />
    </Field>
    <Field label={t('field.pause')} for={form.id('pause')} error={form.show('pause')}>
      <Input id={form.id('pause')} type="number" min="0" step="5" inputmode="numeric" bind:value={form.pause} suffix={t('entries.min')} invalid={!!form.show('pause')} />
    </Field>
  </div>

  <div class="duration" class:empty={form.worked === null}>
    <div class="total">
      <span class="eyebrow">{t('field.duration')}</span>
      <span class="value tabular">{form.rounded ? formatDuration(form.rounded.minutes) : '—'}</span>
    </div>
    <div class="notes">
      {#if form.rounded?.applied && form.roundingDiff !== 0 && form.worked !== null}
        <span class="note">
          <Sparkles size={13} />
          {t('entries.rounded_from', { original: formatDuration(form.worked), diff: `${form.roundingDiff > 0 ? '+' : '−'}${Math.abs(form.roundingDiff)}` })}
        </span>
      {:else if roundingActive(form.rounding)}
        <span class="note quiet">{t('entries.rounding_rule', { mode: t(`entries.rounding_${form.rounding.rounding_mode}`), inc: form.rounding.rounding_increment })}</span>
      {/if}
      {#if form.overnight && !form.isEdit}
        <span class="note warn"><Moon size={13} />{t('entries.overnight_split', { date: formatDate(form.nextDay, 'short') })}</span>
      {/if}
      {#if snap && form.isEdit}
        <Badge tone="info" size="sm">{t('entries.rounding_applied', { mode: t(`entries.rounding_${snap.rounding_mode || 'nearest'}`), inc: snap.rounding_increment ?? '' })}</Badge>
      {/if}
    </div>
  </div>
</EditorSection>

<style>
  .times { display: grid; grid-template-columns: 1.35fr 1fr 1fr 0.9fr; gap: var(--space-3); }
  @media (max-width: 520px) {
    .times { grid-template-columns: 1fr 1fr; }
    .date { grid-column: 1 / -1; }
  }
  .now { padding: 0; border: 0; border-radius: var(--radius-2xs); background: none; color: var(--brand); font-size: var(--text-xs); font-weight: var(--weight-semibold); }
  .now:hover { text-decoration: underline; }

  .duration {
    display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: var(--space-3);
    padding: var(--space-3) calc(var(--space-3) + 2px);
    border: 1px solid var(--border-brand); border-radius: var(--radius); background: var(--brand-softer);
  }
  .duration.empty { background: var(--surface-2); border-color: var(--border); }
  .total { display: flex; flex-direction: column; gap: var(--space-0_5); }
  .value { font-size: var(--text-2xl); font-weight: var(--weight-bold); letter-spacing: var(--tracking-tight); color: var(--text); }
  .notes { display: flex; flex-direction: column; align-items: flex-end; gap: var(--space-1); text-align: right; }
  .note { display: inline-flex; align-items: center; gap: var(--space-1); font-size: var(--text-xs); font-weight: var(--weight-medium); color: var(--brand); }
  .note.quiet { color: var(--text-3); font-weight: var(--weight-regular); }
  .note.warn { color: var(--warning); }
</style>
